// Global Instructions Rule Applied!
import React, { useEffect, useRef } from 'react'
import { List, Empty, Spin, Typography, Button } from 'antd'
import { RecordRow } from './RecordRow.tsx'
import { LoadingState, ErrorState } from './ui.tsx'
import { NormalizedRow } from '../lib/listDescriptors.ts'

interface InfiniteListProps {
  items: NormalizedRow[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  totalCount: number
  onLoadMore: () => void
  onReload: () => void
  onRowClick: (item: NormalizedRow) => void
  emptyText?: string
}

/**
 * Renders a NormalizedRow list with infinite scroll: an IntersectionObserver sentinel
 * auto-loads the next page, and an always-present "Load more" button is the fallback for
 * iOS momentum-scroll flicks that can outrun the observer. Loading / error / empty states
 * reuse the shared mobile ui primitives.
 */
export function InfiniteList({
  items,
  loading,
  loadingMore,
  error,
  hasMore,
  totalCount,
  onLoadMore,
  onReload,
  onRowClick,
  emptyText
}: InfiniteListProps): React.JSX.Element {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore()
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, onLoadMore, items.length])

  // First-page states (nothing to show yet).
  if (loading && items.length === 0) return <LoadingState />
  if (error && items.length === 0) return <ErrorState message={error} onRetry={onReload} />
  if (!loading && items.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText || 'No records'} style={{ marginTop: 48 }} />
  }

  return (
    <div>
      <List
        size="small"
        dataSource={items}
        rowKey={(it) => `${it.module}-${it.id}`}
        renderItem={(it) => <RecordRow item={it} onClick={() => onRowClick(it)} />}
      />
      {/* The footer is also the scroll sentinel; the observer only attaches while hasMore. */}
      <div ref={sentinelRef} style={{ textAlign: 'center', padding: '14px 0 4px' }}>
        {hasMore ? (
          loadingMore ? (
            <Spin size="small" />
          ) : (
            <>
              {error ? (
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text type="warning" style={{ fontSize: 12 }}>
                    Couldn't load more.
                  </Typography.Text>
                </div>
              ) : null}
              <Button size="small" onClick={onLoadMore}>
                {error ? 'Retry' : 'Load more'}
              </Button>
            </>
          )
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {totalCount > items.length ? `${items.length} of ${totalCount}` : `${items.length} ${items.length === 1 ? 'record' : 'records'}`}
          </Typography.Text>
        )}
      </div>
    </div>
  )
}

export default InfiniteList
